/* eslint-disable react/jsx-handler-names */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';

import MODAL_MAP from 'constants/modals';
import { bem } from 'helpers/class-name';
import { usePrevious } from 'helpers/react-hooks';

import './Modal.scss';

/*
  If you're here because you need a modal... We don't use the portal pattern (see `react-modal` for an example).
  We dispatch our modals.

  First, the component to be modal'd needs to be added to the `MODAL_MAP` (ex: 'FooBarModal').

  Then that modal can be opened from a saga or component by `dispatch(openModal('FooBarModal'))`
  The second arg `openModal` accepts is an obj to be passed to the modal component as props
  (ex. openModal('FooBarModal', { biz:'markie' }) --> <FooBarModal biz="markie"/>)

  Only one modal can be open at a time. Opening a second modal replaces the first but any passed props remain.
  Dispatching `openModal('FooBarModal')` again - it will still have the {biz:'markie'} props from the last time.

  We use react-modal (for now) to handle portaling, esc-to-close, etc.  If, for some reason, you need to override
  our settings (you shouldn't), a `modalConfig` property on your component will be passed as props to react-modal.
*/

const DEFAULT_PROPS = {
  isOpen: true,
  shouldCloseOnOverlayClick: true,
  ariaHideApp: process.env.NODE_ENV !== 'test',
};

const { element } = bem('modal');

const Modal = ({ closeModal, modalName, modalProps }) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const previousModal = usePrevious({ modalName, modalProps, closeModal });
  const node = useRef(null);

  const handleRequestClose = prevModalProps => {
    const { onClose } = prevModalProps;
    if (onClose) {
      onClose();
    }
    setIsScrolling(false);
  };

  useEffect(() => {
    if (previousModal) {
      const { modalName: prevModalName, modalProps: prevModalProps } = previousModal;
      if (prevModalName && !modalName) {
        handleRequestClose(prevModalProps);
      }
    }
  }, [modalName]);

  const resizeModal = () => {
    if (!node.current || !node.current?.firstChild) return;

    const modalHeight = node.current.getBoundingClientRect().height;
    const contentHeight = node.current.firstElementChild.getBoundingClientRect().height;

    setIsScrolling(contentHeight > modalHeight);
  };

  useEffect(() => {
    window.addEventListener('resize', resizeModal);

    return () => {
      window.removeEventListener('resize', resizeModal);
    };
  }, []);

  const [isModalBelowDecagon, setIsModalBelowDecagon] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const handleDecagonEvent = event => {
      if (!isMounted) return;

      if (event.eventName === 'DUET_INITIALIZE') {
        setIsModalBelowDecagon(true);
      }
      if (event.eventName === 'DUET_DEINITIALIZE') {
        setIsModalBelowDecagon(false);
      }
    };

    if (window.duet) {
      window.duet.setEventListener(handleDecagonEvent);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  if (!modalName) return null;

  const ModalToRender = MODAL_MAP[modalName];

  if (!ModalToRender) {
    if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
      // eslint-disable-next-line no-console
      console.error(`Cannot find modal ${modalName} in MODAL_MAP`);
    }
    closeModal();
    return null;
  }

  const { overlayClassName, className, ...modalConfig } = ModalToRender.modalConfig || {};

  return (
    <ReactModal
      {...DEFAULT_PROPS}
      overlayClassName={element('overlay', { belowDecagon: isModalBelowDecagon }, overlayClassName)}
      className={element('content', { isScrolling }, className)}
      onRequestClose={closeModal}
      onAfterOpen={resizeModal}
      // FYI: this is ReactModal's interface for React.createRef()
      contentRef={contentNode => {
        node.current = contentNode;
      }}
      {...modalConfig}
      {...modalProps}>
      <ModalToRender {...modalProps} closeModal={closeModal} resizeModal={resizeModal} />
    </ReactModal>
  );
};

Modal.propTypes = {
  closeModal: PropTypes.func,
  modalName: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  modalProps: PropTypes.shape({
    onClose: PropTypes.func,
  }),
};

export default Modal;
